import { isLabel } from 'diagram-js/lib/util/ModelUtil';
import { getLabel } from 'bpmn-js/lib/util/LabelUtil';

const MAX_WIDTH = 300;
const MIN_WIDTH = 20;
const PADDING = 8;
const LINE_HEIGHT = 14;

/**
 * Widens external labels after import.
 *
 * bpmn-js recomputes every external label to a ~90px-wide box on import,
 * regardless of the BPMNLabel bounds in the file, so long names wrap into a
 * narrow, tall column. This behavior measures the actual text and grows each
 * label to fit it (up to MAX_WIDTH), keeping it centred on its current anchor.
 * CustomLabelRenderer then wraps at the wider width, so labels read on as few
 * lines as possible.
 */
export default function LabelWidenBehavior(eventBus, elementRegistry, textRenderer) {
  let measureCanvas = null;

  function ctx2d(style) {
    if (typeof document === 'undefined') return null;
    if (!measureCanvas) measureCanvas = document.createElement('canvas');
    const ctx = measureCanvas.getContext('2d');
    if (!ctx) return null;
    const size = parseInt(style.fontSize, 10) || 12;
    ctx.font = `${style.fontWeight || 'normal'} ${size}px ${style.fontFamily || 'Arial, sans-serif'}`;
    return ctx;
  }

  function wrappedRows(ctx, line, wrapWidth) {
    const words = line.split(/\s+/).filter(Boolean);
    if (!words.length) return 1;
    let rows = 1;
    let current = '';
    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word;
      if (!current || ctx.measureText(candidate).width <= wrapWidth) {
        current = candidate;
      } else {
        rows++;
        current = word;
      }
    }
    return rows;
  }

  function widen(label, ctx) {
    const text = getLabel(label);
    if (!text) return;

    const lines = String(text).split(/\r?\n/);
    const natural = Math.max(...lines.map((l) => ctx.measureText(l).width), 0);
    const target = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.ceil(natural) + PADDING * 2));
    if (target <= Math.round(label.width) + 1) return;

    const wrapWidth = target - PADDING;
    const rows = lines.reduce((sum, line) => sum + wrappedRows(ctx, line, wrapWidth), 0);
    const height = Math.max(LINE_HEIGHT, rows * LINE_HEIGHT + PADDING);

    const centerX = label.x + label.width / 2;
    label.width = target;
    label.x = Math.round(centerX - target / 2);
    label.height = height;
    eventBus.fire('element.changed', { element: label });
  }

  eventBus.on('import.done', () => {
    const ctx = ctx2d(textRenderer.getExternalStyle());
    if (!ctx) return;
    elementRegistry.forEach((element) => {
      if (isLabel(element)) widen(element, ctx);
    });
  });
}

LabelWidenBehavior.$inject = ['eventBus', 'elementRegistry', 'textRenderer'];
